// @flow
import React, { Component, type Node } from 'react';
import { isInteger, noop } from 'lodash';

import { generateUniqueId } from 'Utils/unique-id/uniqueId';
import { getFormattedLabel } from 'Components/utils/form/form.util';
import { ICONS } from 'Components/utils/icon/icons';

import { Icon, type IconName } from 'Components/frameworks/icon/Icon.jsx';
import { FormControl } from 'Components/frameworks/form-inputs/form-control/FormControl.jsx';

import { themes } from './InputText.themes';

type InputTypes = 'email' | 'password' | 'search' | 'tel' | 'text' | 'url';

type Props = {
  autoComplete?: AutoComplete,
  className?: string,
  hasClear?: boolean,
  hasTypeToggler?: boolean,
  iconLeft?: IconName,
  iconRight?: IconName,
  id?: string,
  isActive?: boolean,
  isDisabled?: boolean,
  isFilled?: boolean,
  isLoading?: boolean,
  isNotEditable?: boolean,
  isReadOnly?: boolean,
  isRequired?: boolean,
  label?: string | Node,
  maxLength?: number,
  minLength?: number,
  name?: string,
  notes?: string,
  onBlur?: (SyntheticEvent<>) => void,
  onChange?: (name: string, value: string) => void,
  onClear?: (SyntheticEvent<>) => void,
  onClick?: (SyntheticEvent<>) => void,
  onFocus?: (SyntheticEvent<>) => void,
  onKeyDown?: (SyntheticKeyboardEvent<>) => void,
  onKeyUp?: (SyntheticKeyboardEvent<>) => void,
  pattern?: string,
  placeholder?: string,
  size?: SizeProps,
  theme?: string,
  type?: InputTypes,
  typeToggler?: {
    antiIcon: IconName,
    antiType: InputTypes,
    icon: IconName,
  },
  validationState?: string,
  value?: string,
  dataTestId?: string,
};

type State = {
  type: InputTypes,
  value: string,
};

export class InputText extends Component<Props, State> {
  static defaultProps = {
    autoComplete: 'off',
    size: 'medium',
    theme: 'default',
    type: 'text',
    typeToggler: {
      antiIcon: ICONS.CLOSED_EYE_PASSWORD,
      antiType: 'text',
      icon: ICONS.EYE_PASSWORD,
    },
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      type: props.type || 'text',
      value: props.value || '',
    };
  }

  UNSAFE_componentWillReceiveProps({ value, type }: Props) {
    this.setState(prevState => {
      if (prevState.value !== value || prevState.type !== type) {
        return { type, value };
      }
    });
  }

  onChange = (e: SyntheticEvent<>) => {
    const { isLoading, isReadOnly, name = '', onChange = noop } = this.props;

    if (!isReadOnly && !isLoading) {
      e.persist();

      // $FlowFixMe
      const { value } = e.target;

      this.setState(
        {
          value,
        },
        () => onChange(name, value),
      );
    }
  };

  onKeyDown = (e: SyntheticKeyboardEvent<>) => {
    const { onKeyDown = noop } = this.props;
    e.persist();
    onKeyDown(e);
  };

  onClear = (e: SyntheticEvent<>) => {
    const { onClear = noop } = this.props;

    this.setState(
      {
        value: '',
      },
      () => onClear(e),
    );
  };

  toggleType = () => {
    const { type, typeToggler = {} } = this.props;

    this.setState({ type: this.state.type !== typeToggler.antiType ? typeToggler.antiType : type });
  };

  render() {
    const {
      autoComplete,
      className,
      hasClear,
      hasTypeToggler,
      iconLeft,
      iconRight,
      id = generateUniqueId(
        [typeof this.props.label === 'string' ? this.props.label : ''],
        'InputText_',
      ),
      isActive,
      isDisabled,
      isFilled,
      isLoading,
      isNotEditable,
      isReadOnly,
      isRequired,
      label,
      maxLength,
      minLength,
      name,
      notes,
      onBlur,
      onClick,
      onFocus,
      onKeyUp,
      pattern,
      placeholder,
      size,
      theme = 'default',
      typeToggler = {},
      validationState,
      dataTestId,
    } = this.props;

    const { type, value } = this.state;

    const hasClearEnabled = Boolean(hasClear && value);
    const hasValue = Boolean(value);

    const { thInput, thToggler } = themes[theme](this.props);

    return (
      <FormControl
        className={className}
        hasClear={hasClearEnabled}
        iconLeft={iconLeft}
        iconRight={iconRight}
        id={id}
        isActive={isActive}
        isDisabled={isDisabled}
        isFilled={isFilled && hasValue}
        isLoading={isLoading}
        isReadOnly={isReadOnly}
        isRequired={isRequired}
        label={label}
        notes={notes}
        onClear={this.onClear}
        size={size}
        theme={theme}
        validationState={validationState}
      >
        <input
          autoComplete={autoComplete}
          className={thInput}
          disabled={isDisabled}
          data-testid={dataTestId}
          id={id}
          {...(isInteger(maxLength) ? { maxLength } : null)}
          {...(isInteger(minLength) ? { minLength } : null)}
          {...(name ? { name } : null)}
          onBlur={onBlur}
          onChange={this.onChange}
          onClick={onClick}
          onFocus={onFocus}
          onKeyDown={this.onKeyDown}
          onKeyUp={onKeyUp}
          // $FlowFixMe
          {...(pattern ? { pattern } : null)}
          {...(placeholder ? { placeholder: getFormattedLabel(placeholder, isRequired) } : null)}
          readOnly={Boolean(isReadOnly || isLoading || isNotEditable)}
          required={isRequired}
          role={type === 'search' ? 'search' : 'textbox'}
          type={type}
          value={value}
        />
        {hasTypeToggler && (
          <button
            className={thToggler}
            disabled={isDisabled}
            onClick={this.toggleType}
            type="button"
            data-testid="InputTextToggler"
          >
            <Icon name={type === this.props.type ? typeToggler.icon : typeToggler.antiIcon} />
          </button>
        )}
      </FormControl>
    );
  }
}
